import MarkdownContainer from "../../../shared/markdown/MarkdownContainer";
import ToolsSection from "../tools/ToolsSection";
import { useUiMode } from "../context/UiModeContext";
import { useManage, useReadOnly } from "../context/ManageContext";
import UpgradeHint from "../../../shared/subscription/UpgradeHint";
import { CheckIcon, CopyIcon, EditIcon, RotateCwIcon } from "lucide-react";
import MDEditor from "@uiw/react-md-editor";
import Message from "../../../../i18n/Message";

import "./AboutSection.scss";
import React, { useState } from "react";
import label from "../../../../i18n/label";
import backend from "../../../../backend/backend";
import Notify from "../../../shared/Notify";
import { useIntl } from "react-intl";
import { useDispatch } from "react-redux";
import { loadMcpPlaygrounds } from "../../store/mcpPlaygroundActionCreators";
import Spinner from "../../../../assets/icons/Spinner";
import RegenerateAboutDialog from "./RegenerateAboutDialog";
import PlaygroundAuthType from "../../playgroundAuthType";
import PlaygroundTabs from "../playgroundTabs";

const AboutSection = ({ playground, setTab }) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const { uiMode } = useUiMode();
  const manage = useManage();
  const readOnly = useReadOnly();
  const [editMode, setEditMode] = useState(false);
  const [description, setDescription] = useState(playground.description);
  const [loading, setLoading] = useState(false);
  const [regenerate, setRegenerate] = useState(false);
  const [copyMcpUrlState, setCopyMcpUrlState] = useState(null);

  const onDescriptionSave = () => {
    setLoading(true);

    backend
      .savePlayground({
        ...playground,
        description: description,
      })
      .then((resp) => {
        setLoading(false);
        setEditMode(false);
        dispatch(loadMcpPlaygrounds(intl));
      })
      .catch((err) => {
        console.log("Failed to save playground", err);
        setLoading(false);
        Notify.error(label(intl, "error_unexpected"));
      });
  };

  return (
    <div className={`about_section ${uiMode.toLowerCase()}_mode`}>
      {manage && !editMode && (
        <div className="manage_buttons">
          <button
            className="btn no-btn edit_button"
            disabled={readOnly}
            onClick={() => !readOnly && setRegenerate(true)}
          >
            <RotateCwIcon size={18} />
            <Message id="regenerate" />
          </button>
          <button
            className="btn no-btn edit_button"
            disabled={readOnly}
            onClick={() => !readOnly && setEditMode(true)}
          >
            <EditIcon size={18} />
            <Message id="edit" />
          </button>
          {readOnly && (
            <UpgradeHint messageId="upgrade_hint_readonly" plan={undefined} />
          )}
        </div>
      )}
      {!editMode && (
        <MarkdownContainer>{playground.description}</MarkdownContainer>
      )}
      {editMode && (
        <div className="edit_mode_container">
          <MDEditor
            value={description}
            onChange={setDescription}
            preview={loading ? "preview" : "edit"}
            hideToolbar={false}
            height={500}
            textareaProps={{
              readOnly: loading,
            }}
            data-color-mode={uiMode.toLowerCase()}
          />
          <div className="edit_mode_footer">
            <button
              className="btn btn-outline-dark btn-sm save_button"
              onClick={onDescriptionSave}
              disabled={loading}
            >
              {loading && <Spinner size={16} />}
              {!loading && <Message id="save" />}
              {loading && <Message id="saving" />}
            </button>
            <button
              className="btn no-btn cancel_button"
              onClick={() => {
                setDescription(playground.description);
                setEditMode(false);
              }}
            >
              <Message id="cancel" />
            </button>
          </div>
        </div>
      )}
      {playground.status === "ANALYZED" && (
        <div className={`server_details_section ${uiMode.toLowerCase()}_mode`}>
          <h2>
            <Message id="mcp_server_details_title" />
          </h2>
          <div className="server_details_row">
            <div>
              <Message id="mcp_server_url" />:
            </div>
            <div style={{ display: "flex", alignItems: "center" }}>
              <span>{playground.mcpServerUrl}</span>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  cursor:
                    copyMcpUrlState === playground.mcpServerUrl
                      ? "default"
                      : "pointer",
                  marginLeft: 4,
                  padding: 2,
                  borderRadius: 4,
                  transition: "background 0.2s",
                  userSelect: "none",
                }}
                title="Copy MCP Server URL"
                onClick={async (e) => {
                  e.preventDefault?.();
                  if (copyMcpUrlState === playground.mcpServerUrl) return;
                  setCopyMcpUrlState(playground.mcpServerUrl);
                  try {
                    await navigator.clipboard.writeText(
                      playground.mcpServerUrl
                    );
                  } catch (err) {}
                  setTimeout(() => setCopyMcpUrlState(null), 3000);
                }}
              >
                {copyMcpUrlState === playground.mcpServerUrl ? (
                  <CheckIcon
                    size={16}
                    color="#00c853"
                    style={{ marginLeft: 2 }}
                  />
                ) : (
                  <CopyIcon
                    size={16}
                    color="#67748e"
                    style={{ marginLeft: 2 }}
                  />
                )}
              </div>
            </div>
          </div>
          <div className="server_details_row">
            <div>
              <Message id="mcp_server_type" />:
            </div>
            <div>
              <Message id={`mcp_server_type_${playground.mcpServerType}`} />
            </div>
          </div>
          <div className="server_details_row">
            <div>
              <Message id="widget_auth_title" />:
            </div>
            <div>
              {playground.authType === PlaygroundAuthType.BEARER_TOKEN && (
                <Message id="widget_auth_bearer_token_label" />
              )}
              {playground.authType === PlaygroundAuthType.URL_PARAM && (
                <Message id="widget_auth_url_param_label" />
              )}
              {(!playground.authType ||
                playground.authType === PlaygroundAuthType.NONE) && (
                <Message id="widget_auth_no_auth_label" />
              )}
            </div>
          </div>
          <div className="server_details_row">
            <div
              className="server_details_connect_link"
              onClick={() => {
                setTab(PlaygroundTabs.CONNECT);
                setTimeout(() => {
                  document
                    .querySelector(".playground_widget")
                    .scrollIntoView({ behavior: "smooth" });
                }, 100);
              }}
            >
              <Message id="widget_server_details_connect_link" />
            </div>
          </div>
        </div>
      )}
      {playground.toolsJson && <ToolsSection playground={playground} />}
      {regenerate && (
        <RegenerateAboutDialog
          playground={playground}
          onClose={() => setRegenerate(false)}
        />
      )}
    </div>
  );
};

export default AboutSection;
